import type { ScopeOption } from '../filters/types.js';
import type { CatalogGroup, WorkflowCard } from './workflows.types.js';

export const ALL = 'all';

const ACRONYMS = new Set(['ua']);

export interface CatalogFilterState {
  search: string;
  role: string;
  stage: string;
  category: string;
  outcome: string;
}

export interface CatalogFilterOptions {
  roles: ScopeOption[];
  stages: ScopeOption[];
  categories: ScopeOption[];
  outcomes: ScopeOption[];
}

export const EMPTY_FILTERS: CatalogFilterState = {
  search: '',
  role: ALL,
  stage: ALL,
  category: ALL,
  outcome: ALL,
};

export function humanize(value: string): string {
  return value
    .split('_')
    .map((word) =>
      ACRONYMS.has(word) ? word.toUpperCase() : word.charAt(0).toUpperCase() + word.slice(1),
    )
    .join(' ');
}

function optionsOf(values: string[], allLabel: string): ScopeOption[] {
  return [
    { id: ALL, label: allLabel },
    ...[...new Set(values)].sort().map((value) => ({ id: value, label: humanize(value) })),
  ];
}

function matchesQuery(card: WorkflowCard, query: string): boolean {
  if (query === '') return true;
  return [card.name, card.description, humanize(card.role), humanize(card.category)].some((text) =>
    text.toLowerCase().includes(query),
  );
}

export function deriveFilterOptions(groups: CatalogGroup[]): CatalogFilterOptions {
  const cards = groups.flatMap((group) => group.items);
  return {
    roles: optionsOf(
      cards.map((card) => card.role),
      'All roles',
    ),
    stages: [
      { id: ALL, label: 'All stages' },
      ...groups
        .filter((group) => group.items.length > 0)
        .map((group) => ({ id: group.stage, label: group.label })),
    ],
    categories: optionsOf(
      cards.map((card) => card.category),
      'All categories',
    ),
    outcomes: optionsOf(
      cards.flatMap((card) => card.outcomes),
      'All outcomes',
    ),
  };
}

export function applyFilters(groups: CatalogGroup[], filters: CatalogFilterState): CatalogGroup[] {
  const query = filters.search.trim().toLowerCase();
  return groups
    .filter((group) => filters.stage === ALL || group.stage === filters.stage)
    .map((group) => ({
      ...group,
      items: group.items.filter(
        (card) =>
          matchesQuery(card, query) &&
          (filters.role === ALL || card.role === filters.role) &&
          (filters.category === ALL || card.category === filters.category) &&
          (filters.outcome === ALL || card.outcomes.includes(filters.outcome)),
      ),
    }))
    .filter((group) => group.items.length > 0);
}
